import { useCallback, useEffect, useRef, useState } from "react";

import api from "../../../lib/api";

/*
 * What a car owes before it can be released, and the one action that clears it.
 */

const num = (value) => Number(value ?? 0) || 0;

function isCancel(err) {
  return err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
}

function isPaymentRequired(err) {
  const status = err?.response?.status
  return status === 402
}

function payErrorMessage(err) {
  const code = err?.data?.error ?? err?.response?.data?.error ?? null;

  if (isPaymentRequired(err)) {
    return (
      err?.data?.message ??
      err?.response?.data?.message ??
      "The card was declined. Take payment at the desk instead."
    );
  }

  if (code === "PAYMENT_NO_CARD_ON_FILE") {
    return "No card on file for this booking. Take payment at the desk.";
  }

  if (code === "PAYMENT_IN_PROGRESS") {
    return "A charge for this booking is already in progress.";
  }

  return "Could not charge the card. Try again, or take payment at the desk.";
}

export default function usePickupCharge(bookingId, { at, scope } = {}) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(Boolean(bookingId));
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [settledAmount, setSettledAmount] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const payInFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) {
      setView(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const next = await api.valetCharge(bookingId, {
        at,
        signal: controller.signal,
      });
      if (!mountedRef.current || controller.signal.aborted) return;
      setView(next);
    } catch (err) {
      if (!mountedRef.current || isCancel(err)) return;
      setError(err);
    } finally {
      if (mountedRef.current && !controller.signal.aborted) setLoading(false);
    }
  }, [bookingId, at]);

  useEffect(() => {
    setPayError(null);
    // Another car, another balance: last car's receipt must not carry over.
    setSettledAmount(null);
    load();
  }, [load]);

  const pay = useCallback(async () => {
    if (payInFlightRef.current || !bookingId) return "failed";

    payInFlightRef.current = true;
    setPaying(true);
    setPayError(null);

    /* What this scope will actually take, so the receipt names that and not
     * the whole balance. */
    const charged =
      scope === "STAY"
        ? num(view?.charge?.stayOutstandingAud)
        : num(view?.charge?.outstandingAud);

    try {
      const next = await api.payValetBalance(bookingId, { scope });
      if (!mountedRef.current) return "failed";
      setView(next);
      setSettledAmount(charged);
      return "paid";
    } catch (err) {
      if (!mountedRef.current || isCancel(err)) return "failed";

      const code = err?.data?.error ?? err?.response?.data?.error ?? null;

      if (code === "PAYMENT_NOT_APPLICABLE") {
        load();
        return "paid";
      }

      if (code === "PAYMENT_NO_CARD_ON_FILE") return "needs-card";

      setPayError(payErrorMessage(err));
      return "failed";
    } finally {
      payInFlightRef.current = false;
      if (mountedRef.current) setPaying(false);
    }
  }, [bookingId, load, view, scope]);

  /* Nothing is taken from a card here: the desk is saying the balance to date
   * reached the guest's folio, and their name goes on that figure. Pressing it
   * again simply reprices and re-stamps, so there is no in-progress guard
   * beyond the one that stops a double-click. */
  const postToRoom = useCallback(async () => {
    if (payInFlightRef.current || !bookingId) return "failed";

    payInFlightRef.current = true;
    setPaying(true);
    setPayError(null);

    try {
      await api.queueBookingPayment(bookingId);
      if (!mountedRef.current) return "failed";
      await load();
      return "posted";
    } catch (err) {
      if (!mountedRef.current || isCancel(err)) return "failed";

      setPayError(
        "Could not record the room posting. Try again, or confirm it on the Parking Audit page.",
      );
      return "failed";
    } finally {
      payInFlightRef.current = false;
      if (mountedRef.current) setPaying(false);
    }
  }, [bookingId, load]);

  const charge = view?.charge ?? null;
  const payment = view?.payment ?? null;
  const outstanding = Number(charge?.outstandingAud ?? 0) || 0;

  /*
   * The stay on its own. A parked car is fee-bearing the moment it is priced
   * (see FEE_BEARING_STATUSES in api/src/valet_sessions/charge.ts), so the
   * whole balance carries a pickup nobody has asked for; the fees belong to
   * the pickup that incurs them, which is where they are taken.
   */
  const stayOutstanding = num(charge?.stayOutstandingAud);

  /*
   * The pickups the guest actually took. These counts come from the requests
   * themselves rather than the fee total, so the parked car's pending pickup
   * is not among them.
   */
  const billed = Math.max(0, Math.round(num(charge?.breakdown?.valetRequestChargeableCount)));
  const free = Math.max(0, Math.round(num(charge?.breakdown?.valetRequestFreeCount)));
  const unit = num(charge?.breakdown?.valetRequestFeeUnitAud);

  const pickups = {
    billed,
    free,
    total: billed + free,
    unit,
    /* What those pickups cost, whoever settles them. */
    feesAud: billed * unit,
  };

  /* The fee the pickup being lodged right now will incur. It is already inside
   * `outstanding`, so the screen that quotes it names the rest apart. */
  const pendingRequestFee = num(charge?.breakdown?.valetRequestFeePendingAud);
  /* Present on every response, and `applies` only on a chargeback card - the
   * one shape the desk needs to decide between a Charge button and a quote. */
  const hotelInvoiced = charge?.hotelInvoiced ?? null;

  const posting = view?.posting ?? null;
  const postableTotal = num(hotelInvoiced?.stayAud) + pickups.feesAud;

  return {
    charge,
    payment,
    hasCharges: Boolean(charge?.details),
    breakdown: charge?.breakdown ?? null,
    hotelInvoiced,
    hotelInvoices: hotelInvoiced?.applies === true,
    posting,
    posted: posting?.status === "CONFIRMED",
    postableTotal,
    currency: charge?.currency ?? payment?.currency ?? "AUD",
    outstanding,
    stayOutstanding,
    pickups,
    pendingRequestFee,
    outstandingBeforePickup: Math.max(0, outstanding - pendingRequestFee),
    pricedTo: charge?.pricedTo ?? null,
    hasCardOnFile: Boolean(payment?.hasCardOnFile),
    settled: settledAmount !== null,
    settledAmount,
    loading,
    error,
    paying,
    payError,
    clearPayError: () => setPayError(null),
    pay,
    postToRoom,
    retry: load,
  };
}
